import { useModal } from '@ebay/nice-modal-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import messageIcon from '../../../assets/free-bonus/message.webp'
import messageIconLight from '../../../assets/free-bonus/message_light.webp'
import treasureBox from '../../../assets/free-bonus/treasure-box.webp'
import treasureBoxLight from '../../../assets/free-bonus/treasure-box-light.webp'
import useClaimTreasureBonus from '../../../hooks/mutations/useClaimTreasureBonus'
import { useAvailableBonuses } from '../../../hooks/queries/useAvailableBonuses'
import useUnreadMessages from '../../../hooks/useUnreadMessages'
import { useBoundStore } from '../../../store/store'
import classNames from '../../../utils/classNames'
import InboxModal from '../../inbox/inbox-modal'
import { TailSpin } from '../../loaders'
import { GlowButton } from '..'

// Only the rescue bonus surfaces today/yesterday counts; other bonuses show
// just their label.
const COUNTS_BONUS_NAME = 'rescue_bonus'

const HEADING_TEXT =
  'font-bold text-xl xs:text-2xl whitespace-nowrap [-webkit-background-clip:text] bg-clip-text [-webkit-text-fill-color:transparent] [text-fill-color:transparent] text-transparent bg-[linear-gradient(180deg,#FFFFFF_0%,#BEDFFB_100%)] drop-shadow-[0_4px_4px_#088EE0] dark:drop-shadow-none dark:bg-[linear-gradient(180deg,#FAE896_0%,#CE981D_100%)]'

export const COUNT_BADGE_CLASS =
  'absolute top-1 -right-1 bg-[#ff4040] text-white text-[9px] xs:text-[10px] font-bold rounded-full min-w-[14px] h-[14px] xs:min-w-4 xs:h-4 px-1 flex items-center justify-center'

const FreeBonus = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(['freeBonus', 'walletManagement'])

  const theme = useBoundStore(state => state.common.theme)

  const { noOfUnread } = useUnreadMessages()
  const { groupedBonuses, isLoading: isRefetchingBonuses } =
    useAvailableBonuses()

  const {
    claim: claimBonus,
    isLoading: isClaiming,
    variables: claimingId,
  } = useClaimTreasureBonus()

  const inboxModal = useModal(InboxModal)

  // Layered over the current page rather than routed to /profile/inbox, so
  // closing the inbox leaves the page (and any running game) untouched.
  const openInbox = () => {
    onClose()
    inboxModal.show()
  }

  return (
    <div className="relative px-5 pt-7 pb-4 rounded-xl bg-[linear-gradient(90deg,#FFFFFF_0%,#0DAAED_100%)] dark:bg-primary-bg dark:bg-size-primary-bg border border-solid border-white dark:border-[rgba(245,197,66,0.35)] shadow-[0_4px_24px_rgba(0,0,0,0.5),0_0_20px_rgba(0,166,255,0.08)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.5),0_0_20px_rgba(245,197,66,0.08)]">
      <div className="flex gap-3 mb-3">
        <img
          className="w-[160px] aspect-square object-contain -ml-10 -mt-10 z-10"
          src={theme === 'light' ? treasureBoxLight : treasureBox}
          alt="Treasure Box"
        />
        <div>
          <div className={HEADING_TEXT}>{t('inbox')}</div>

          <button
            type="button"
            onClick={openInbox}
            className={classNames(
              'relative rounded-lg p-3 hover:scale-[1.02]',
              theme === 'light'
                ? 'border border-solid border-white bg-[#42a5f5]'
                : 'container-glow bg-[#000]',
            )}>
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={theme === 'light' ? messageIconLight : messageIcon}
                  alt=""
                  className="w-4 h-4 object-contain xs:w-5 xs:h-5"
                />
                {noOfUnread > 0 && (
                  <span className={COUNT_BADGE_CLASS}>{noOfUnread}</span>
                )}
              </div>
              <p className="text-white text-sm xs:text-base">{t('messages')}</p>
            </div>
          </button>

          <div className={HEADING_TEXT}>{t('free-bonus-header')}</div>
        </div>
      </div>

      {groupedBonuses.map(group => {
        const isRowClaiming = isClaiming && claimingId === group.claimId

        return (
          <div
            key={group.bonusName}
            className={classNames(
              'mb-3 relative rounded-lg pl-2 py-2 xs:py-3 flex items-center h-[60px]',
              theme === 'light'
                ? 'border border-solid border-white bg-[linear-gradient(180deg,#67BAF5_0%,#2B7CD6_100%)]'
                : 'container-glow bg-[#000]',
            )}>
            <img
              className="w-[45px] h-[45px] object-contain mr-2"
              src={group.icon}
              alt=""
            />
            <div className="flex-1 min-w-0 mr-2">
              <span className="block capitalize text-white text-[12px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                {group.label}
              </span>
              {group.bonusName === COUNTS_BONUS_NAME && (
                <div className="flex items-center gap-1.5 mt-1">
                  {[
                    {
                      key: 'today',
                      label: t('today'),
                      count: group.todayCount,
                    },
                    {
                      key: 'yesterday',
                      label: t('yesterday'),
                      count: group.yesterdayCount,
                    },
                  ].map(({ key, label, count }) => (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[9px] font-medium leading-none border border-white/40 bg-white/15 text-white dark:border-[rgba(245,197,66,0.35)] dark:bg-[rgba(245,197,66,0.12)] dark:text-[#F5C542]">
                      {label}
                      <span className="font-bold text-white dark:text-[#FAE896]">
                        {count}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mr-2">
              <GlowButton
                disabled={isClaiming || isRefetchingBonuses}
                onClick={() => claimBonus(group.claimId)}
                className="capitalize lg:hover:opacity-80 !w-[80px] xs:!w-[90px] text-white font-bold rounded-lg px-1 py-2.5 text-[10px]">
                {isRowClaiming ? (
                  <TailSpin color="#fff" width={16} height={16} />
                ) : (
                  t('claim').toLowerCase()
                )}
              </GlowButton>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Memoized so opening and closing the popup — which re-renders the wrapper on
// every toggle — does not re-render the bonus list underneath it. Its only prop
// is the wrapper's useCallback-stable `onClose`, so the memo holds; it still
// re-renders on its own triggers (the bonus/unread queries, claim state, theme).
export default memo(FreeBonus)
